// Visit The Stimulus Handbook for more details 
// https://stimulusjs.org/handbook/introduction
// 
// This example controller works with specially annotated HTML like:
//
// <div data-controller="hello">
//   <h1 data-target="hello.output"></h1>
// </div>

import { Controller } from "@hotwired/stimulus"
import Sortable from 'sortablejs';

export default class extends Controller {
  static values = {
    type: { type: String, default: 'input_holder' }, // Can be 'input_holder', 'file', 'struct_response', or 'onboarding_step'
    url: { type: String, default: '' },              // URL template for the move endpoint
    handleSelector: { type: String, default: '' }   // Optional specific handle selector
  }

  connect() {
    // Initialize Sortable with different options based on type
    const options = {
      animation: 150,
      ghostClass: 'sortable-ghost',
      chosenClass: 'sortable-chosen',
      dragClass: 'sortable-drag',
      fallbackOnBody: true,
      fallbackTolerance: 4,
      bubbleScroll: true,
      dragoverBubble: false,
      onEnd: this.onDragEnd.bind(this),
      onStart: this.onDragStart.bind(this),
      onUnchoose: this.onDragCleanup.bind(this),
      // Don't revert on failed requests - we'll handle reverting manually if needed
      revertOnSpill: false,
      removeOnSpill: false,
      // Force fallback to prevent issues with native HTML5 drag and drop
      forceFallback: true,
      // Prevent default sorting behavior - we'll handle it manually
      sort: true,
      // Store the sortable instance for later use
      store: {
        get: (sortable) => {
          return [];
        },
        set: (sortable) => {
          // Do nothing - we handle persistence ourselves
        }
      }
    };

    // Use different handle selectors based on type
    if (this.typeValue === 'file') {
      // First check if there's a custom handle selector provided
      if (this.hasHandleSelectorValue && this.handleSelectorValue) {
        options.handle = this.handleSelectorValue;
      } else {
        // Check the structure to determine the appropriate handle
        const firstDraggable = this.element.querySelector('[data-id]');
        if (firstDraggable) {
          if (firstDraggable.querySelector('.drag-handle')) {
            // For file fields with explicit drag handles
            options.handle = '.drag-handle';
          } else if (firstDraggable.classList.contains('photo-item')) {
            // For listing photos, use the drag indicator as handle
            options.handle = '.drag-indicator';
          }
        } else {
          // Default to using the whole element as handle
          // This is used for photo-item elements in listings
        }
      }
    } else if (this.typeValue === 'struct_response') {
      // For struct responses, use the drag handle
      options.handle = '.drag-handle';
    } else if (this.typeValue === 'onboarding_step') {
      // For onboarding steps, use custom handle selector if provided, otherwise drag from anywhere
      if (this.hasHandleSelectorValue && this.handleSelectorValue) {
        options.handle = this.handleSelectorValue;
      }
      // Otherwise no specific handle needed - the entire row is draggable
    } else {
      // For input_holder, use the first td with the ellipsis icons
      options.handle = 'td:first-child';
      options.draggable = 'tr[data-id]';
    }

    this.sortable = new Sortable(this.element, options);

  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy();
    }
  }

  onDragEnd(event) {
    this.toggleNestedSortables(false);

    // Don't process if item didn't actually move
    if (event.oldIndex === event.newIndex) {
      return;
    }

    // Handle differently based on the type
    if (this.typeValue === 'file') {
      this.handleFileReordering(event);
    } else if (this.typeValue === 'struct_response') {
      this.handleStructResponseReordering(event);
    } else if (this.typeValue === 'onboarding_step') {
      this.handleOnboardingStepReordering(event);
    } else {
      this.handleInputHolderReordering(event);
    }
  }

  onDragStart(event) {
    this.toggleNestedSortables(true, event.item);
  }

  onDragCleanup() {
    this.toggleNestedSortables(false);
  }

  toggleNestedSortables(disabled, activeItem = null) {
    if (this.typeValue !== 'input_holder') return;

    const scope = activeItem || this.element;
    scope.querySelectorAll('[data-controller~="drag"]').forEach((nestedElement) => {
      if (nestedElement === this.element) return;

      const nestedSortable = Sortable.get(nestedElement);
      if (nestedSortable) {
        nestedSortable.option('disabled', disabled);
      }
    });
  }

  handleFileReordering(event) {
    const resourceId = this.element.dataset.resourceId;
    // Get all items in their new order based on structure
    let items;
    let fileIds;

    if (this.element.tagName === 'UL') {
      // For file_field.html.erb structure with li elements
      items = Array.from(this.element.querySelectorAll('li[data-id]'));
    } else if (this.element.classList.contains('row')) {
      // For listings/photos.html.erb structure with div.photo-item elements
      items = Array.from(this.element.querySelectorAll('.photo-item[data-id]'));
    } else {
      // Fallback to any elements with data-id attribute
      items = Array.from(this.element.querySelectorAll('[data-id]'));
    }

    fileIds = items.map(item => item.dataset.id);
    // Send the new order to the server
    const url = this.element.dataset.dragUrl;
    const formData = new FormData();
    formData.append('resource_id', resourceId);

    // Add each file ID as a separate form parameter with the same name
    fileIds.forEach(id => {
      formData.append('file_ids[]', id);
    });

    fetch(url, {
      method: 'PATCH',
      body: formData,
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(text => {
            try {
              // Try to parse as JSON
              const json = JSON.parse(text);
              throw new Error(json.error || `Server error: ${response.status} ${response.statusText}`);
            } catch (e) {
              // If parsing fails, use the raw text
              throw new Error(`Server error: ${response.status} ${response.statusText} - ${text}`);
            }
          });
        }
        return response.json();
      })
      .then(data => {
      })
      .catch(error => {
        // Optionally revert the drag operation or show an error message
        alert(`Failed to reorder files: ${error.message}`);
      });
  }

  handleStructResponseReordering(event) {
    // Prevent default revert behavior
    event.preventDefault();

    // Get the ID of the dragged element
    const id = event.item.dataset.id;

    // Check if ID exists
    if (!id) {
      alert("Error: No ID found on dragged element");
      // Revert the drag since we can't process it
      this.sortable.sort(this.sortable.toArray());
      return;
    }

    // Use the newIndex from the event which is more reliable
    const newPosition = event.newIndex;

// Store original order in case we need to revert
    const originalOrder = this.sortable.toArray();
    const originalDOMOrder = Array.from(this.element.children);

    // Send the new order to the server
    const url = this.element.dataset.dragUrl.replace(':id', id);
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    if (!csrfToken) {
      alert("Error: No CSRF token found");
      // Revert the drag
      this.revertOrder(originalDOMOrder);
      return;
    }

    const formData = new FormData();

    // Add 1 to convert from 0-indexed to 1-indexed position
    const position = Math.max(1, newPosition + 1);
    formData.append('position', position);

    // Disable sorting while we wait for the server response
    this.sortable.option("disabled", true);

    fetch(url, {
      method: 'PATCH',
      body: formData,
      headers: {
        'X-CSRF-Token': csrfToken
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(text => {
            try {
              const json = JSON.parse(text);
              throw new Error(json.error || `Server error: ${response.status} ${response.statusText}`);
            } catch (e) {
              throw new Error(`Server error: ${response.status} ${response.statusText} - ${text}`);
            }
          });
        }
        return response.json();
      })
      .then(data => {
        // Update the display numbers
        const items = Array.from(this.element.querySelectorAll('.response-item:not(.new-response)'));
        items.forEach((item, index) => {
          const numberSpan = item.querySelector('.card-header span span');
          if (numberSpan) {
            numberSpan.textContent = numberSpan.textContent.replace(/#\d+/, '#' + (index + 1));
          }
        });

        // Re-enable sorting
        this.sortable.option("disabled", false);

        // Force update the data-id order for Sortable
        this.updateSortableOrder();
      })
      .catch(error => {
        // Re-enable sorting
        this.sortable.option("disabled", false);

        // Revert the visual order
        this.revertOrder(originalDOMOrder);

        alert(`Failed to reorder: ${error.message}`);
      });
  }

  // Helper method to revert DOM order
  revertOrder(originalDOMOrder) {
    this.element.innerHTML = '';
    originalDOMOrder.forEach(child => this.element.appendChild(child));
    this.updateSortableOrder();
  }

  // Helper method to update Sortable's internal order tracking
  updateSortableOrder() {
    if (this.sortable) {
      const order = Array.from(this.element.children).map(el => el.dataset.id);
      this.sortable.sort(order);
    }
  }

  handleOnboardingStepReordering(event) {
    // Get the ID of the dragged element
    const id = event.item.dataset.id;

    // Check if ID exists
    if (!id) {
      alert("Error: No ID found on dragged element");
      return;
    }

    // Use the newIndex from the event which is more reliable
    const newPosition = event.newIndex;

    // Use the URL value if provided, otherwise fall back to dataset
    const urlTemplate = this.hasUrlValue ? this.urlValue : this.element.dataset.dragUrl;

    if (!urlTemplate) {
      alert("Error: No URL template found");
      return;
    }

    const url = urlTemplate.replace(':id', id);
    const formData = new FormData();

    // Add 1 to convert from 0-indexed to 1-indexed position
    const position = Math.max(1, newPosition + 1);
    formData.append('order', position);

    // Store original order in case we need to revert
    const originalDOMOrder = Array.from(this.element.children);

    // Disable sorting while we wait for the server response
    this.sortable.option("disabled", true);

    fetch(url, {
      method: 'PATCH',
      body: formData,
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(text => {
            try {
              // Try to parse as JSON
              const json = JSON.parse(text);
              throw new Error(json.error || `Server error: ${response.status} ${response.statusText}`);
            } catch (e) {
              // If parsing fails, use the raw text
              throw new Error(`Server error: ${response.status} ${response.statusText} - ${text}`);
            }
          });
        }
        // Try to parse JSON response
        return response.json().catch(() => ({}));
      })
      .then(data => {
        // Re-enable sorting
        this.sortable.option("disabled", false);
      })
      .catch(error => {
        // Re-enable sorting
        this.sortable.option("disabled", false);

        // Revert the visual order
        this.revertOrder(originalDOMOrder);

        alert(`Failed to reorder: ${error.message}`);
      });
  }

  handleInputHolderReordering(event) {
    // Get the ID of the dragged element
    const id = event.item.dataset.id;

    // Get the new position (0-indexed)
    const newPosition = Array.from(this.element.children).indexOf(event.item);

    // Send the new order to the server
    const url = this.element.dataset.dragUrl.replace(':id', id);
    const formData = new FormData();

    // Add 1 to convert from 0-indexed to 1-indexed position
    // The server expects positions to start from 1, not 0
    // Also ensure the position is never less than 1
    const position = Math.max(1, newPosition + 1);
    formData.append('position', position);

    fetch(url, {
      method: 'PATCH',
      body: formData,
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(text => {
            try {
              // Try to parse as JSON
              const json = JSON.parse(text);
              throw new Error(json.error || `Server error: ${response.status} ${response.statusText}`);
            } catch (e) {
              // If parsing fails, use the raw text
              throw new Error(`Server error: ${response.status} ${response.statusText} - ${text}`);
            }
          });
        }
        // Handle empty response from head :ok
        return response.json().catch(() => ({}));
      })
      .then(data => {
      })
      .catch(error => {
        // Revert the drag operation or show an error message
        alert(`Failed to reorder: ${error.message}`);
        // You might want to reload the page to reset the order
        // window.location.reload();
      });
  }
}
